import Seo from "../components/Seo";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Bell, BellRing, CalendarDays, Check, Eye, Info, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatUSD } from "../lib/format";
import { Button } from "../components/ui/button";
import PortfolioChart from "../components/PortfolioChart";
import PortfolioComposition from "../components/PortfolioComposition";
import PortfolioMovements from "../components/PortfolioMovements";
import PortfolioStrategy from "../components/PortfolioStrategy";
import PortfolioPaywall from "../components/PortfolioPaywall";
import AdminReaderPreview, { PORTFOLIO_VIEWS, readerViewLabel } from "../components/AdminReaderPreview";
import TierPill from "../components/TierBadge";
import { TIER_LABEL, fmtDateFr, fmtIntFr, fmtNumFr, fmtPctFr } from "../lib/portfolioMeta";
import { tierStyle } from "../lib/tiers";

const DISCLAIMER =
  "Portefeuille réel géré par Side B et publié à but éducatif. Il ne constitue ni un conseil en investissement, ni une recommandation personnalisée : il ne tient pas compte de votre situation, de vos objectifs ou de votre tolérance au risque. Les performances passées ne préjugent pas des performances futures. Investir en crypto-actifs comporte un risque de perte en capital.";

function useTierPrice() {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  return (tier) => {
    const entry = cfg?.tiers?.[tier];
    return entry ? `${entry.price_eur.toString().replace(".", ",")} €/mois` : null;
  };
}

function Kpi({ label, value, hint, tone = "default" }) {
  const cls =
    tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : tone === "muted" ? "text-zinc-500" : "text-zinc-100";
  return (
    <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-2 text-2xl font-display font-black font-mono-num ${cls}`}>{value}</div>
      <div className="mt-1 text-[11px] text-zinc-500">{hint}</div>
    </div>
  );
}

function MoneyCard({ label, value, sub, tone = "default" }) {
  const cls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : "text-zinc-100";
  return (
    <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1.5 text-xl font-display font-black font-mono-num ${cls}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

// Pastille d'offre aux couleurs du site (Gratuit gris, Essentiel orange, Pro vert, Max violet)
function TierBadge({ tier, locked }) {
  const t = tier || "free";
  return <TierPill tier={t} locked={!!locked && t !== "free"} />;
}

export default function SideBPortfolioDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = !!(user && user.role === "admin");

  // Aperçu admin : simule un visiteur, un membre gratuit ou un abonné (Essentiel, Pro, Max)
  const [previewAs, setPreviewAs] = useState("admin");
  const isPreviewing = isAdmin && previewAs !== "admin";
  const adminView = isAdmin && !isPreviewing;
  const previewParam = isPreviewing ? previewAs : null;
  const viewerTier = isPreviewing
    ? ["essentiel", "pro", "max"].includes(previewAs)
      ? previewAs
      : "free"
    : user?.subscription_tier || "free";
  const loggedIn = isPreviewing ? previewAs !== "guest" : !!user;
  const priceOf = useTierPrice();

  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["sideb-portfolio", slug, previewParam],
    queryFn: async () =>
      (await api.get(`/sideb/portfolios/${slug}`, { params: previewParam ? { preview_as: previewParam } : undefined })).data,
    retry: false,
  });

  const followMut = useMutation({
    mutationFn: async () => {
      if (p.is_following) return (await api.delete(`/sideb/portfolios/${p.id}/follow`)).data;
      return (await api.post(`/sideb/portfolios/${p.id}/follow`)).data;
    },
    onSuccess: (r) => {
      toast.success(r.following ? "Vous suivez ce portefeuille" : "Suivi arrêté");
      qc.invalidateQueries({ queryKey: ["sideb-portfolio", slug] });
      qc.invalidateQueries({ queryKey: ["sideb-portfolios"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="h-8 w-1/3 bg-white/5 rounded animate-pulse mb-6" />
        <div className="h-64 bg-white/5 rounded animate-pulse" />
      </div>
    );
  }
  if (isError || !p) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display font-black text-3xl mb-3">Portefeuille introuvable</h1>
        <Link to="/portefeuilles-sideb" className="text-emerald-400 text-sm">
          ← Retour aux portefeuilles
        </Link>
      </div>
    );
  }

  const m = p.metrics || {};
  const up = (m.total_return_pct ?? 0) >= 0;
  const locked = !!p.is_locked;
  const tierName = TIER_LABEL[p.tier_required] || p.tier_required;
  const money = adminView ? p.money : null;
  const startDate = m.start_date;

  // Bouton d'action de l'en-tête
  let action;
  if (locked) {
    const isUpgrade = viewerTier !== "free";
    const price = priceOf(p.tier_required);
    action = (
      <Button asChild className={`h-10 ${tierStyle(p.tier_required).cta}`} data-testid="sideb-header-unlock">
        <Link to="/upgrade">
          {isUpgrade ? `Passer à ${tierName}` : "Débloquer"}
          {price ? ` · ${price}` : ""} <ArrowRight className="ml-1 w-4 h-4" />
        </Link>
      </Button>
    );
  } else if (!loggedIn) {
    action = (
      <div className="text-right">
        <Button asChild className="h-10 bg-emerald-500 text-black hover:bg-emerald-300" data-testid="sideb-follow-login">
          <Link to="/login">
            <Bell className="w-4 h-4 mr-1.5" /> Connexion pour suivre
          </Link>
        </Button>
        <div className="mt-1.5 text-[11px] text-zinc-500">Alerte à chaque mouvement</div>
      </div>
    );
  } else {
    action = (
      <div className="text-right">
        <Button
          onClick={() => (isPreviewing ? toast.info("Bouton désactivé pendant l'aperçu") : followMut.mutate())}
          disabled={followMut.isPending}
          variant={p.is_following ? "outline" : "default"}
          className={
            p.is_following
              ? "h-10 border-white/10 bg-transparent hover:bg-white/5"
              : "h-10 bg-emerald-500 text-black hover:bg-emerald-300"
          }
          data-testid="sideb-follow-btn"
        >
          {p.is_following ? (
            <>
              <Check className="w-4 h-4 mr-1.5" /> Suivi
            </>
          ) : (
            <>
              <Bell className="w-4 h-4 mr-1.5" /> Suivre
            </>
          )}
        </Button>
        <div className="mt-1.5 text-[11px] text-zinc-500">
          {p.is_following ? "Vous êtes prévenu à chaque mouvement" : "Alerte à chaque mouvement"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="sideb-detail">
      <Seo title={p.name} description={p.description || undefined} path={`/portefeuilles-sideb/${p.slug}`} />
      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-6xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : vous voyez le portefeuille comme
              <b className="font-semibold text-white">{readerViewLabel(previewAs, PORTFOLIO_VIEWS)}</b>
            </span>
            <button
              type="button"
              onClick={() => setPreviewAs("admin")}
              className="text-violet-300 hover:text-white underline-offset-4 hover:underline"
            >
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}

      <div className="border-b border-white/5">
        <div className="mx-auto max-w-6xl px-6 pt-6 pb-7 sm:py-10">
          <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-4 sm:mb-6" aria-label="Fil d'Ariane">
            <Link to="/portefeuilles-sideb" className="hover:text-emerald-400">
              Portefeuilles
            </Link>
            <span>/</span>
            <span className="text-zinc-300">{p.name}</span>
          </nav>
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300 mb-2">Portefeuille Side B</div>
              <div className="flex items-start justify-between gap-3">
                <h1 className="font-display font-black text-[2rem] sm:text-5xl leading-tight">{p.name}</h1>
                {/* Téléphone : cloche discrète à côté du titre (au lieu du gros bouton) */}
                {!locked && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!loggedIn) return navigate("/login");
                      if (isPreviewing) return toast.info("Bouton désactivé pendant l'aperçu");
                      followMut.mutate();
                    }}
                    disabled={followMut.isPending}
                    title={p.is_following ? "Ne plus suivre" : "Suivre : alerte à chaque mouvement"}
                    aria-label={p.is_following ? "Ne plus suivre ce portefeuille" : "Suivre ce portefeuille"}
                    aria-pressed={!!p.is_following}
                    className={`sm:hidden mt-1 shrink-0 grid place-items-center w-10 h-10 rounded-full border transition-colors ${
                      loggedIn && p.is_following
                        ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400"
                        : "border-white/10 text-zinc-400"
                    }`}
                    data-testid="sideb-follow-bell"
                  >
                    {loggedIn && p.is_following ? <BellRing className="w-[18px] h-[18px]" fill="currentColor" /> : <Bell className="w-[18px] h-[18px]" />}
                  </button>
                )}
              </div>
              {p.description && <p className="mt-3 text-zinc-400">{p.description}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-500">
                {startDate && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" /> Lancé le {fmtDateFr(startDate)}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> {p.followers_count ?? 0}{" "}
                  {(p.followers_count ?? 0) > 1 ? "personnes suivent" : "personne suit"} ce portefeuille
                </span>
                <TierBadge tier={p.tier_required} locked={locked} />
              </div>
            </div>
            <div className={locked ? "" : "hidden sm:block"}>{action}</div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Phrase simple pour les débutants : traduit l'indice base 10 000 */}
        <div
          className="rounded-xl border border-white/5 bg-gradient-to-r from-emerald-500/[0.07] to-transparent px-6 py-5 flex flex-wrap items-center gap-x-6 gap-y-2"
          data-testid="sideb-hero-sentence"
        >
          <p className="text-zinc-300">
            10 000 $ investis au lancement{startDate ? `, le ${fmtDateFr(startDate)},` : ""} vaudraient aujourd'hui
          </p>
          <p className={`font-display font-black text-3xl font-mono-num ${up ? "text-emerald-400" : "text-rose-400"}`}>
            {fmtIntFr(m.index_final)} $
          </p>
          <span className={`text-sm font-mono-num ${up ? "text-emerald-300" : "text-rose-300"}`}>
            ({fmtPctFr(m.total_return_pct)})
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sideb-kpi-grid">
          <Kpi label="Performance totale" value={fmtPctFr(m.total_return_pct)} hint="Depuis le lancement" tone={up ? "up" : "down"} />
          <Kpi
            label="Rendement annualisé"
            value={m.cagr_pct == null ? "—" : fmtPctFr(m.cagr_pct)}
            hint={m.cagr_pct == null ? "Disponible après 1 an" : "Moyenne par an"}
            tone={m.cagr_pct == null ? "muted" : "default"}
          />
          <Kpi
            label="Pire baisse"
            value={`−${fmtNumFr(m.max_drawdown_pct ?? 0)} %`}
            hint="Plus forte chute depuis un sommet"
            tone="down"
          />
          <Kpi
            label="Volatilité 30 j"
            value={`${fmtNumFr(m.volatility_30d_pct ?? 0)} %`}
            hint="Amplitude des variations, sur un an"
          />
        </div>

        {money && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sideb-money-grid">
            <MoneyCard
              label="Valeur actuelle"
              value={formatUSD(money.current_value_usd)}
              sub={`Apports ${formatUSD(money.total_deposits_usd)} · Retraits ${formatUSD(money.total_withdrawals_usd)}`}
            />
            <MoneyCard
              label="Plus/moins-value totale"
              value={formatUSD(money.pnl_usd)}
              tone={(money.pnl_usd ?? 0) >= 0 ? "up" : "down"}
              sub={`Latente ${formatUSD(money.unrealized_pnl_usd)} · Encaissée ${formatUSD(money.realized_pnl_usd)} · dont revenus ${formatUSD(money.income_usd)}`}
            />
            <MoneyCard
              label="Capital encore investi"
              value={formatUSD(money.net_invested_usd)}
              sub={`Capital retiré ${formatUSD(money.capital_withdrawn_usd)} · Apports implicites ${formatUSD(money.auto_funded_usd)}`}
            />
            <MoneyCard
              label="Source"
              value={p.import_source === "coinbase" ? "Coinbase" : p.import_source === "bitpanda" ? "Bitpanda" : "Saisie manuelle"}
              sub={p.last_import_date ? `Dernier mouvement importé : ${fmtDateFr(p.last_import_date)}` : "Aucun import CSV"}
            />
          </div>
        )}

        {adminView && p.warnings && p.warnings.length > 0 && (
          <div className="mt-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="sideb-warnings">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-amber-300 mb-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Points d'attention du calcul · visible par vous seul
            </div>
            <ul className="space-y-1 text-[12px] text-amber-200/80 list-disc pl-5">
              {p.warnings.slice(0, 10).map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6">
          <PortfolioChart navCurve={p.nav_curve || []} valueCurve={p.history || []} ath={m.ath} showValueToggle={adminView} />
        </div>

        <div className="mt-6">
          <PortfolioStrategy
            text={p.strategy_text}
            horizon={p.horizon}
            riskProfile={p.risk_profile}
            rebalancing={p.rebalancing}
          />
        </div>

        <div className="mt-6">
          {locked ? (
            <PortfolioPaywall tier={p.tier_required} currentTier={viewerTier} loggedIn={loggedIn} />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <PortfolioComposition allocation={p.allocation || []} positions={p.positions || []} adminView={adminView} />
              <PortfolioMovements
                portfolioId={p.id}
                events={p.events_public || []}
                adminView={adminView}
                canalUrl={p.canal_url || null}
                maxPrice={priceOf("max")}
              />
            </div>
          )}
        </div>

        <div
          className="mt-10 flex gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3 text-xs text-zinc-500 leading-relaxed"
          data-testid="sideb-disclaimer"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <p>{DISCLAIMER}</p>
        </div>
      </div>

      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} views={PORTFOLIO_VIEWS} />}
    </div>
  );
}
